import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

/**
 * Переключатель темы: светлая · тёмная · как в системе.
 *
 * ТРИ СОСТОЯНИЯ, А НЕ ДВА. «Как в системе» — это не то же самое, что «светлая»:
 * телефон сам переключается вечером, и человек, выбравший системную, ждёт
 * именно этого. Поэтому выбор хранится как `light` / `dark` / отсутствие
 * записи, а не как булев флаг.
 *
 * Атрибут ставится на <html>, а палитра целиком живёт в index.css: компоненты
 * про тему ничего не знают и не обязаны знать.
 */
const KEY = 'urbanpurr.theme'
const MODES = ['system', 'light', 'dark']

export function readTheme() {
  try {
    const v = localStorage.getItem(KEY)
    return v === 'light' || v === 'dark' ? v : 'system'
  } catch {
    return 'system'
  }
}

export function applyTheme(mode) {
  const root = document.documentElement
  if (mode === 'system') root.removeAttribute('data-theme')
  else root.setAttribute('data-theme', mode)
  try {
    if (mode === 'system') localStorage.removeItem(KEY)
    else localStorage.setItem(KEY, mode)
  } catch {
    // Приватный режим: тема продержится до конца сессии, и это приемлемо.
  }
}

function Icon({ mode }) {
  const common = { width: 18, height: 18, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round', 'aria-hidden': true }
  if (mode === 'light') {
    return (
      <svg {...common}>
        <circle cx="12" cy="12" r="4" />
        <path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" />
      </svg>
    )
  }
  if (mode === 'dark') {
    return (
      <svg {...common}>
        <path d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8z" />
      </svg>
    )
  }
  return (
    <svg {...common}>
      <rect x="2" y="4" width="20" height="13" rx="2" />
      <path d="M8 21h8M12 17v4" />
    </svg>
  )
}

export default function ThemeSwitch({ className = '' }) {
  const { t } = useTranslation()
  const [mode, setMode] = useState(readTheme)

  useEffect(() => applyTheme(mode), [mode])

  const next = () => setMode((m) => MODES[(MODES.indexOf(m) + 1) % MODES.length])

  return (
    <button
      type="button"
      onClick={next}
      // Название режима читается скринридером и видно в подсказке: иконка луны
      // сама по себе не говорит, включена тема или только предлагается.
      title={t(`theme.${mode}`)}
      aria-label={t('theme.switch', { mode: t(`theme.${mode}`) })}
      className={`flex size-9 cursor-pointer items-center justify-center rounded-full border-[1.5px] border-edge text-ink transition hover:border-ink ${className}`}
    >
      <Icon mode={mode} />
    </button>
  )
}
